import {Component, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthSessionsService} from 'src/app/shared/services/auth-sessions.service';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexLegend,
  ApexPlotOptions,
  ApexResponsive,
  ApexXAxis,
  ChartComponent
} from "ng-apexcharts";
import * as moment from 'moment';
import * as L from 'leaflet';
import {UtilsService} from 'src/app/shared/services/utils.service';
import {GeocodingService} from 'src/app/shared/services/geocoding.service';
import {environment} from 'src/environments/environment';
import {AuthSessionModel} from "../../shared/models/auth-session.model";
import {CircleMarker} from "leaflet";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  responsive: ApexResponsive[];
  xaxis: ApexXAxis;
  legend: ApexLegend;
  fill: ApexFill;
};

@Component({
  selector: 'app-sessions',
  templateUrl: './sessions.component.html',
  styleUrls: ['./sessions.component.scss']
})
export class SessionsComponent implements OnInit {

  private locationMaps = [];

  sessionsData: Array<any>;
  sessionDetailsMap: Map<string, AuthSessionModel> = new Map<string, AuthSessionModel>();
  data: any[] = [];
  selected = [];

  maxDate: Date;
  from: Date;
  to: Date;
  ranges: any;
  startDateRange: Date[];
  INITIAL_ACCORDIONS_EXPANDED = 3;

  isLoading: boolean = false;
  accordionExpanded: boolean[];
  showMap: boolean = true;

  sessionLocationMapAvailable = {};

  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private utilsService: UtilsService,
    private geocodingService: GeocodingService,
    private authSessionsService: AuthSessionsService
  ) {
  }

  ngOnInit(): void {
    this.initializeDatePicker();
    this.loadSessions();
  }

  onLoadTest() {
    this.loadSessions();
  }

  loadSessions() {

    this.isLoading = true;
    this.accordionExpanded = [];
    this.locationMaps = [];
    this.authSessionsService.fetchAuthenticationSessions(this.from, this.to)
      .subscribe(
        resp => {
          this.data = resp.authSessions;
          this.showMap = resp.showMap
          this.loadSessionDetails();
          this.loadActivity();
          this.isLoading = false;
        },
        error => {
          this.isLoading = false;
          console.log(error);
        }
      );

  }

  loadActivity() {
    this.authSessionsService.fetchActivity()
      .subscribe(
        resp => {
          this.isLoading = false;
          this.sessionsData = resp.sessionsTimeChart?.data;
          if(this.sessionsData) {
            this.loadMonthlyChart();
          }
        },
        error => {
          this.isLoading = false;
          console.log(error);
        }
      );
  }

  loadMonthlyChart() {

    let successfulArray = this.sessionsData.map(sessionObj => sessionObj.authenticated);
    let failedArray = this.sessionsData.map(sessionObj => sessionObj.failed);
    let labelArray = this.sessionsData.map(sessionObj => {
      // let diff = this.timeTo - this.timeFrom;
      // let gap = diff < (24 * 3600 * 1000) ? "hour" : "other";
      let gap = "other";

      var dt = moment(sessionObj.ts);
      if (gap === "hour") {
        return dt.hours() + ":00";
      } else {
        return dt.format("DD MMM YYYY");
      }
    });


    this.chartOptions = {
      series: [
        {
          name: "SUCCESSFUL",
          data: successfulArray
        },
        {
          name: "FAILED",
          data: failedArray
        }
      ],
      chart: {
        type: "bar",
        height: 350,
        stacked: true,
        toolbar: {
          show: true
        },
        zoom: {
          enabled: true
        }
      },
      plotOptions: {
        bar: {
          horizontal: false
        }
      },
      xaxis: {
        type: "category",
        categories: labelArray
      },
      legend: {
        //position: "right",
        offsetY: 40
      },
      fill: {
        opacity: 1,
        colors: ['#1fb6a4', '#e54d56']
      }
    };


  }

  initializeDatePicker() {
    this.initDateRange();
    this.maxDate = new Date();
    this.from = new Date(new Date().setDate(new Date().getDate() - 1));
    this.to = new Date();
  }

  initDateRange() {
    this.ranges = [{
      value: [new Date(new Date().setDate(new Date().getDate() - 7)), new Date()],
      label: 'Last 7 Days'
    }, {
      value: [new Date(new Date().setDate(new Date().getDate() - 30)), new Date()],
      label: 'Last 30 Days'
    }];

    this.startDateRange = [new Date(new Date().setDate(new Date().getDate() - 365)), new Date()];
  }

  onSubmitSimpleSearch() {
    this.loadSessions();
  }

  dateChangedEvent(dates: Date[]) {
    dates[1].setHours(23, 59, 59, 999);
    this.from = dates[0];
    this.to = dates[1];
    this.onSubmitSimpleSearch();
  }

  isNullOrEmpty(s: string) {
    return this.utilsService.isNullOrEmpty(s);
  }

  openedSessionDetails(session, sessionNumber) {

    this.getSessionDetails(session?.id);
    if (this.locationMaps[sessionNumber]) {
      if(this.sessionLocationMapAvailable[sessionNumber] == null || !this.sessionLocationMapAvailable[sessionNumber]){
        this.sessionLocationMapAvailable[sessionNumber] = false;
      }
      return;
    }

    if (this.showMap) {
      let map = L.map('map_' + sessionNumber, {
        zoomControl: true,
        doubleClickZoom: false,
        scrollWheelZoom: false,
        dragging: false,
        attributionControl: false,
      });
      this.locationMaps[sessionNumber] = true;

      map.on('load', function () {
        setTimeout(() => {
          map.invalidateSize();
        }, 1);
      });

      if (!session.location.coordinates) {
        session.location.coordinates = {
          latitude: 0,
          longitude: 0
        };
      }

      let lat = session.location.coordinates.latitude;
      let long = session.location.coordinates.longitude;

      if ((!lat && !long) || (lat == 0 && long == 0)) {
        if (
          session.location.locationMap['countryCode'] === 'XB' ||
          session.location.locationMap['countryCode'] === 'LAN'
        ) {
          this.sessionLocationMapAvailable[sessionNumber] = false;
          return;
        }
        this.geocodingService
          .fetchGeocodingData(session.location.locationMap)
          .subscribe((results) => {
            if (!results || results.length === 0)
            {
              this.sessionLocationMapAvailable[sessionNumber] = false;
              return;
            }

            session.location.coordinates.latitude = results[0].lat;
            session.location.coordinates.longitude = results[0].lon;
            this.sessionLocationMapAvailable[sessionNumber] = true;
            if (results[0].bbox) {
              this.displayMapViewWithBounds(
                map,
                results[0].bbox["lat1"],
                results[0].bbox["lon1"],
                results[0].bbox["lat2"],
                results[0].bbox["lon2"])
            } else {
              this.displayMapView(
                map,
                results[0].lat,
                results[0].lon
              );
            }
          });
      } else {
        this.sessionLocationMapAvailable[sessionNumber] = true;
        this.displayMapView(map, lat, long);
      }
    }
  }

  displayMapView(map, lat, long) {

    let boxLimit = 1;
    let roundLat = Math.round(lat);
    let roundLong = Math.round(long);
    let box = {}
    let depth = 0;
    let depthLimit = 2;

    do {
      let precisionFactor = Math.pow(10, depth);
      boxLimit = (1 / precisionFactor);

      roundLat = Math.round(lat * precisionFactor) / precisionFactor;
      roundLong = Math.round(long * precisionFactor) / precisionFactor;
      box = {
        "lat1": roundLat - boxLimit,
        "long1": roundLong - boxLimit,
        "lat2": roundLat + boxLimit,
        "long2": roundLong + boxLimit
      }

      depth = depth + 1;
    }
    while ((Math.abs(lat - roundLat) > Number.EPSILON || Math.abs(long - roundLong) > Number.EPSILON) && depth <= depthLimit);

    this.displayMapViewWithBounds(map, box["lat1"], box["long1"], box["lat2"], box["long2"])
  }

  displayMapViewWithBounds(map, lat1, long1, lat2, long2) {

    // delay for UI map controller to resize. without this map zoom is not working properly
    setTimeout(() => {
      const tiles = L.tileLayer(
        environment.TILE_SERVER_URL + environment.GEOAPIFY_API_KEY
      );
      tiles.addTo(map);
      let marker1 = L.marker([lat1, long1]);
      let marker2 = L.marker([lat2, long2]);
      let group = L.featureGroup([marker1, marker2]);
      map.fitBounds(group.getBounds());
      let circleMarker = this.getCircleMarker(lat1 - (lat1 - lat2) / 2, long1 - (long1 - long2) / 2);
      this.zoomAction(map, circleMarker);
    }, 100);
  }

  private zoomAction(map, circleMarker: CircleMarker<any>) {

    function calcRadiusForZoom(originalRadius, originalZoom, zoom) {
      return originalRadius * Math.pow(2, zoom - originalZoom);
    }

    let originalZoom = map.getZoom();
    let originalRadius = 80;
    circleMarker.setRadius(originalRadius);
    circleMarker.addTo(map)

    map.on('zoomend', function (event) {
      var currentZoom = map.getZoom();
      circleMarker.setRadius(calcRadiusForZoom(originalRadius, originalZoom, currentZoom))
    });
  }

  private getCircleMarker(centerLan, centerLong) {
    return L.circleMarker([centerLan, centerLong], {radius: 10, color: '#6495ED', weight: 0.7, fillOpacity: 0.1});
  }

  getScoreReasonExplanations(scoreReason) {
    return Object.keys(scoreReason);
  }

  getCriteriaMet(criteriaMet) {
    return Object.keys(criteriaMet);
  }

  loadSessionDetails() {
    for (let i = 0; i < this.data.length; i++) {
      if (i < this.INITIAL_ACCORDIONS_EXPANDED) {
        const session = this.data[i] as AuthSessionModel;
        this.getSessionDetails(session.id);
        this.accordionExpanded[i] = true;
      } else {
        this.accordionExpanded[i] = false;
      }
    }

  }

  getSessionDetails(sessionId: string) {
    if (!this.sessionDetailsMap.has(sessionId)) {
      this.authSessionsService.getSessionDetails(sessionId).subscribe(value => {
        this.sessionDetailsMap.set(sessionId, value);
      })
    }
  }

  getSession(sessionId) {
    return this.sessionDetailsMap.get(sessionId);
  }
}
